import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart } from 'lucide-react';
import { givingOptions } from '../mockData';

export const Give = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(128, 0, 32, 0.85), rgba(128, 0, 32, 0.85)), url('https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=1200')`
        }}
      >
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h1 className="text-5xl font-bold mb-4">Give Online</h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7] mb-4">
            "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver."
          </p>
          <p className="text-lg text-[#fef3c7]">2 Corinthians 9:7</p>
        </div>
      </section>

      {/* Givelify Integration Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Make Your Donation</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
            <p className="text-lg text-gray-700 mb-8">
              We use Givelify for secure online giving. Click the button below to give through our secure donation platform.
            </p>
          </div>

          <Card className="border-2 border-[#800020] bg-[#fffbeb]">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-white" />
              </div>
              
              <h3 className="text-2xl font-bold text-[#800020] mb-4">
                Give Through Givelify
              </h3>
              
              <p className="text-gray-700 mb-8">
                Givelify makes it easy to give securely from your phone or computer. Your donation will be processed safely and you'll receive an instant receipt for tax purposes.
              </p>

              {/* Givelify Button - Replace with actual Givelify button code when available */}
              <div className="mb-6">
                <a 
                  href="https://www.givelify.com/givenow/1.0/MzQ1NQ==/selection"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button 
                    size="lg"
                    className="bg-[#D4AF37] hover:bg-[#B8860B] text-white text-xl py-8 px-12 font-bold"
                  >
                    <Heart className="h-6 w-6 mr-3" />
                    Give Now with Givelify
                  </Button>
                </a>
              </div>

              <p className="text-sm text-gray-600">
                First time using Givelify? It only takes a minute to set up!
              </p>
            </CardContent>
          </Card>

          {/* Giving Categories */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-[#800020] mb-6 text-center">Ways to Give</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {givingOptions.map((option) => (
                <Card key={option.id} className="border-2 border-[#800020] hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <h4 className="text-xl font-bold text-[#800020] mb-2">{option.name}</h4>
                    <p className="text-gray-700">{option.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Give Section */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Why Your Giving Matters</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-3">Support Ministry</h3>
                <p className="text-gray-700">
                  Your tithes and offerings enable us to carry out God's work, support staff, and maintain our facilities for worship and fellowship.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-3">Reach Community</h3>
                <p className="text-gray-700">
                  Your giving helps us reach our community with the Gospel through outreach programs, community events, and mission work.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-2 border-[#800020]">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-[#800020] mb-3">Grow Kingdom</h3>
                <p className="text-gray-700">
                  Together, we're building God's kingdom, supporting missions, and making an eternal impact in lives around the world.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Other Ways to Give</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>

          <div className="space-y-6">
            <Card className="border-2 border-[#800020]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#800020] mb-2">In-Person Giving</h3>
                <p className="text-gray-700">
                  You may place your gift in the offering baskets during service.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#800020]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#800020] mb-2">Mail Your Gift</h3>
                <p className="text-gray-700 mb-2">
                  You can mail checks to:
                </p>
                <p className="text-gray-700 font-semibold">
                  Mt. Olive Missionary Baptist Church<br />
                  P.O. Box 6334<br />
                  Americus, GA 31709
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#800020]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#800020] mb-2">Planned Giving</h3>
                <p className="text-gray-700">
                  Consider including Mt. Olive in your estate planning. Contact the church office to learn more about legacy giving opportunities.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};
